function formatMoney(cents) {
  return `$${(cents / 100).toFixed(2)}`;
}

class QuickAddBuilderFooter extends HTMLElement {
  constructor() {
    super();
    this.selectedItems = [];
    this.minItems = parseInt(this.getAttribute('min-items')) || 2;
    this.maxItems = this.getAttribute('max-items')
      ? parseInt(this.getAttribute('max-items'))
      : null;
    this.offerId = this.getAttribute('data-offer-id');
    this.rewardValueCents = Math.round(
      (parseFloat(this.getAttribute('data-reward-value')) || 0) * 100
    );
    this.bonusItems = this.hasAttribute('data-bonus-items')
      ? parseInt(this.getAttribute('data-bonus-items'), 10)
      : null;
    this.bonusDiscountPercent = parseFloat(this.getAttribute('data-bonus-discount-percent')) || 0;
    // "buy N, get the next one free" offers: the trailing slot(s) of the strip carry a text
    // label instead of counting toward the price. 0 when the section isn't in free-item mode,
    // which leaves every code path below untouched.
    this.freeItems = parseInt(this.getAttribute('data-free-items'), 10) || 0;
    this.freeItemLabel = this.getAttribute('data-free-item-label') || 'FREE';
    this.bonusProgressMessage =
      this.getAttribute('data-bonus-progress-message') ||
      'Add {remaining} more to unlock your discount';
    this.bonusUnlockedMessage =
      this.getAttribute('data-bonus-unlocked-message') || 'Discount unlocked!';
    this.editCtaText = this.getAttribute('data-edit-cta-text') || 'Update Cart';
    this.revealed = false;
    this.addingToCart = false;

    // 'new' (default add-to-cart flow) or 'edit' — flipped on by quick-add-builder's cart
    // prefill (see quick-add-builder.js _prefillFromCart) when this offer is already in the
    // cart, so shoppers arriving via the cart drawer's Edit link can change their selection.
    this.builderType = 'new';
    this.bundleToEdit = [];

    // Latest verdict from <discount-code-status>, when the section runs a coupon-backed
    // offer. Null until it reports — the footer never waits on it.
    this.discountState = null;

    this.onAddToCartClick = this.onAddToCartClick.bind(this);
    this.onRemoveClick = this.onRemoveClick.bind(this);
    this.handleCartUpdate = this.handleCartUpdate.bind(this);
    this.handleDiscountStatus = this.handleDiscountStatus.bind(this);
    this.onScroll = this.revealFooter.bind(this);
  }

  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.footerEl = this.querySelector('.quick-add-builder-footer');
    this.selectedContainer = this.querySelector('.quick-add-builder-footer__selected_items');
    this.countLabel = this.querySelector('.quick-add-builder-footer__count');
    this.priceOriginal = this.querySelector('.quick-add-builder-footer__price--original');
    this.priceDiscounted = this.querySelector('.quick-add-builder-footer__price--discounted');
    this.savingsLabel = this.querySelector('.quick-add-builder-footer__savings');
    this.freeSavingsLabel = this.querySelector('.quick-add-builder-footer__free-savings');
    this.discountEcho = this.querySelector('[data-discount-echo]');
    this.addToCartButton = this.querySelector('[data-action="add-to-cart"]');
    this.defaultCtaText = this.addToCartButton?.textContent.trim();

    this.footerEl?.classList.add('quick-add-builder-footer--pending-reveal');

    this.addToCartButton?.addEventListener('click', this.onAddToCartClick);
    this.selectedContainer?.addEventListener('click', this.onRemoveClick);
    window.addEventListener('scroll', this.onScroll, { passive: true, once: true });

    this.updateUI();

    window.addEventListener('cartUpdateEnriched', this.handleCartUpdate);
    // Bubbles up from <discount-code-status> elsewhere in the section.
    document.addEventListener('abc:discount-status', this.handleDiscountStatus);

    const initial = window.__abc?.ShopifyCartData;
    if (initial && initial.cart) {
      this.handleCartUpdate({ detail: initial });
    }
  }

  disconnectedCallback() {
    this.addToCartButton?.removeEventListener('click', this.onAddToCartClick);
    this.selectedContainer?.removeEventListener('click', this.onRemoveClick);
    window.removeEventListener('scroll', this.onScroll);

    window.removeEventListener('cartUpdateEnriched', this.handleCartUpdate);
    document.removeEventListener('abc:discount-status', this.handleDiscountStatus);
    this.initialized = false;
  }

  /**
   * Mirrors the discount banner's verdict into the sticky footer — but only the failure. The
   * banner sits at the top of the card and scrolls away; a code that didn't attach must stay
   * on screen while the shopper is still picking. Everything else would just duplicate the
   * banner in the one place that's always visible.
   */
  handleDiscountStatus(event) {
    const detail = event.detail || {};
    this.discountState = detail;

    if (this.discountEcho) {
      const showEcho = detail.state === 'blocked' && detail.message;
      this.discountEcho.textContent = showEcho ? detail.message : '';
      this.discountEcho.hidden = !showEcho;
    }

    this.updateUI();
  }

  handleCartUpdate(event) {
    const enriched = event.detail || {};
    this.cart = enriched.cart || {};
    this.offerInCart =
      Array.isArray(this.cart.items) &&
      this.cart.items.some(
        (item) => item.properties != null && item.properties._offer === this.offerId
      );

    this.updateUI();
  }

  updateProperties({ selectedItems = [] } = {}) {
    this.selectedItems = selectedItems;
    this.updateUI();
  }

  /**
   * Switches this footer into edit mode: the next Add To Cart click removes `cartItems`
   * (the existing line items for this offer) before adding the shopper's updated selection,
   * instead of adding on top of them. Called by quick-add-builder's cart prefill when this
   * offer is already in the cart.
   * @param {Array} cartItems — the cart's current line items tagged with this offer
   */
  setEditMode(cartItems) {
    this.builderType = 'edit';
    this.bundleToEdit = cartItems;
    this.updateUI();
  }

  revealFooter() {
    if (this.revealed) return;
    this.revealed = true;
    this.footerEl?.classList.remove('quick-add-builder-footer--pending-reveal');
  }

  _buildSelectedItemEl(item, { isFree = false } = {}) {
    const wrapper = document.createElement('div');
    wrapper.className = 'quick-add-builder-footer__selected_item';
    if (isFree) wrapper.classList.add('quick-add-builder-footer__selected_item--free');
    wrapper.setAttribute('data-dynamic', 'true');

    const imgWrap = document.createElement('div');
    imgWrap.className = 'quick-add-builder-footer__selected_item--image';
    imgWrap.style.backgroundColor = item.productColor;
    const img = document.createElement('img');
    img.src = item.productImage;
    img.alt = item.productTitle;
    imgWrap.appendChild(img);

    const removeButton = document.createElement('button');
    removeButton.type = 'button';
    removeButton.className = 'select_item_action quick-add-builder-footer__remove-icon';
    removeButton.setAttribute('data-action', 'remove');
    removeButton.setAttribute('data-product-id', item.productId);
    removeButton.setAttribute('data-variant-id', item.variantId);
    removeButton.setAttribute('aria-label', `Remove ${item.productTitle} from selected products`);
    removeButton.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" width="9" height="8" viewBox="0 0 9 8" fill="none" aria-hidden="true">
          <path d="M7.71137 0.356079L0.77832 7.2888" stroke="white"/>
          <path d="M7.71137 7.28821L0.77832 0.355469" stroke="white"/>
        </svg>`;

    wrapper.append(imgWrap, removeButton);

    // a filled free slot keeps its label, so the freebie stays identifiable once chosen
    if (isFree) {
      const badge = document.createElement('span');
      badge.className = 'quick-add-builder-footer__free-badge';
      badge.textContent = this.freeItemLabel;
      wrapper.appendChild(badge);
    }

    return wrapper;
  }

  /**
   * Which selected tile carries the FREE label, or -1 for none.
   *
   * Shopify's Buy X Get Y discounts the CHEAPEST eligible line, not a positional one, so the
   * badge has to follow price rather than sit on the last slot. Scans the whole selection —
   * the offer caps at one free item per order (usesPerOrderLimit: 1), so a shopper who adds
   * beyond the strip still gets exactly one, on whatever is cheapest overall.
   *
   * Below a complete set nothing is free yet, so the badge stays where Liquid put it: on the
   * trailing empty placeholder, where it reads as the incentive to keep going.
   *
   * @param {Array} flatItems — selection expanded so each unit is its own entry
   * @param {number} slotCount — static placeholder slots rendered by the section
   */
  _freeIndex(flatItems, slotCount) {
    if (this.freeItems === 0) return -1;
    if (flatItems.length < slotCount) return -1;

    let cheapest = 0;
    for (let i = 1; i < flatItems.length; i++) {
      if (flatItems[i].price < flatItems[cheapest].price) cheapest = i;
    }
    return cheapest;
  }

  _buildPlaceholderEl() {
    // clone a plain slot, never a free one — an appended overflow slot isn't free
    const template =
      this.selectedContainer.querySelector(
        '.placeholder-item:not(.quick-add-builder-footer__selected_item--free)'
      ) || this.selectedContainer.querySelector('.placeholder-item');
    if (!template) return null;
    const clone = template.cloneNode(true);
    clone.classList.remove('quick-add-builder-footer__selected_item--free');
    clone.querySelector('.quick-add-builder-footer__free-badge')?.remove();
    clone.style.display = '';
    clone.setAttribute('data-dynamic', 'true');
    return clone;
  }

  updateUI() {
    if (!this.selectedContainer) return;

    this.selectedContainer.querySelectorAll('[data-dynamic="true"]').forEach((el) => el.remove());

    const flatItems = this.selectedItems.flatMap((item) => Array(item.quantity).fill(item));
    const placeholders = Array.from(this.selectedContainer.querySelectorAll('.placeholder-item'));

    const freeIdx = this._freeIndex(flatItems, placeholders.length);

    placeholders.forEach((ph, i) => {
      if (i < flatItems.length) {
        ph.style.display = 'none';
        ph.insertAdjacentElement(
          'beforebegin',
          this._buildSelectedItemEl(flatItems[i], { isFree: i === freeIdx })
        );
      } else {
        ph.style.display = '';
      }
    });

    // once every placeholder slot is filled, any additional selected items
    // (beyond min-items) get appended to the end of the strip instead of being dropped
    flatItems.slice(placeholders.length).forEach((item, offset) => {
      const index = placeholders.length + offset;
      this.selectedContainer.appendChild(
        this._buildSelectedItemEl(item, { isFree: index === freeIdx })
      );
    });

    // once every static placeholder is filled, keep open slot(s) visible at the end of the
    // strip so shoppers see they can keep adding (up to max-items, if set). Below the bonus
    // threshold, reveal every slot up through it in one go so the discount target is visible
    // as soon as it's in reach. Once reached (or if no bonus is configured), fall back to
    // keeping exactly one open slot ahead. All slots render identically — no special styling
    // on the bonus slot itself; the progress message above communicates the unlock.
    //
    // Free-item offers opt out: once the free slot is filled the offer is complete, so a
    // trailing "add one more" slot would undercut it. Shoppers can still add beyond the free
    // slot via the product card steppers — those items append to the strip above.
    if (this.freeItems === 0 && flatItems.length >= placeholders.length) {
      const remainingCapacity =
        this.maxItems != null ? Math.max(this.maxItems - flatItems.length, 0) : Infinity;
      const nextMilestone =
        this.bonusItems != null && flatItems.length < this.bonusItems
          ? this.bonusItems
          : flatItems.length + 1;
      const slotsWanted = Math.min(nextMilestone - flatItems.length, remainingCapacity);

      for (let i = 1; i <= slotsWanted; i++) {
        const placeholder = this._buildPlaceholderEl();
        if (placeholder) this.selectedContainer.appendChild(placeholder);
      }
    }

    // Scroll to the last dynamic element, not just the last filled one — when there's an
    // open placeholder slot at the end (per the loop above), that's the one we want in view,
    // so the strip prompts shoppers to keep adding instead of looking "full" and done.
    const dynamicEls = this.selectedContainer.querySelectorAll('[data-dynamic="true"]');
    const lastItemEl = dynamicEls[dynamicEls.length - 1];
    const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    lastItemEl?.scrollIntoView({
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
      inline: 'end',
      block: 'nearest',
    });

    const totalQty = this.selectedItems.reduce((sum, i) => sum + i.quantity, 0);
    const qualified = totalQty >= this.minItems;
    // Buy X Get Y needs the "buys" items AND the "gets" item in the cart — a buy-3-get-1
    // offer only fires at 4. Checking out at 3 would pay full price with the FREE badge still
    // on screen, so the CTA waits for the free slot to be filled. `qualified` deliberately
    // stays on minItems: it drives the progress message below, which is what explains the
    // disabled button ("Nice! Add your free 4th pack.").
    const ctaQualified = totalQty >= this.minItems + this.freeItems;

    if (totalQty > 0) this.revealFooter();
    this.footerEl?.classList.toggle('quick-add-builder-footer--qualified', qualified);

    if (this.countLabel) {
      if (this.bonusItems != null && qualified) {
        // once qualified, swap the count for a progress message counting down to the bonus
        // unlock (e.g. "Add 2 more for 15% off your order"), then the unlocked message once hit
        const remaining = Math.max(this.bonusItems - totalQty, 0);
        this.countLabel.textContent =
          remaining > 0
            ? this.bonusProgressMessage.replace('{remaining}', remaining)
            : this.bonusUnlockedMessage;
      } else {
        this.countLabel.textContent = `${totalQty} / ${this.minItems} Selected`;
      }
    }

    if (totalQty > 0) {
      const rawProductsTotal = this.selectedItems.reduce((sum, i) => sum + i.price * i.quantity, 0);
      const productsOriginalTotal = this.selectedItems.reduce(
        (sum, i) => sum + (i.compareAtPrice || i.price) * i.quantity,
        0
      );
      // once the bonus threshold is reached, the percent discount applies on top of the raw
      // product total — this is what the shopper actually pays for the products themselves
      const bonusUnlocked =
        this.bonusItems != null && totalQty >= this.bonusItems && this.bonusDiscountPercent > 0;
      const discountedTotal = bonusUnlocked
        ? Math.round(rawProductsTotal * (1 - this.bonusDiscountPercent / 100))
        : rawProductsTotal;
      // compare-at always includes the reward's value, so it reads at least that much higher than the actual price
      const originalTotal =
        Math.max(productsOriginalTotal, rawProductsTotal) + this.rewardValueCents;
      const hasSavings = originalTotal > discountedTotal;

      if (this.priceOriginal) {
        this.priceOriginal.hidden = !hasSavings;
        this.priceOriginal.textContent = hasSavings ? formatMoney(originalTotal) : '';
      }
      if (this.priceDiscounted) this.priceDiscounted.textContent = formatMoney(discountedTotal);

      if (this.savingsLabel) {
        const savingsPct = hasSavings ? Math.round((1 - discountedTotal / originalTotal) * 100) : 0;
        this.savingsLabel.textContent = savingsPct > 0 ? `Total Savings ${savingsPct}%` : '';
      }

      // The total above stays pre-coupon — the discount is applied by Shopify at checkout, not
      // here, and quoting a reduced total before the code is confirmed would over-promise. So
      // the free item's value is stated separately: as a forward promise while the set is just
      // assembled, as a confirmed fact once the cart reports the code applicable.
      if (this.freeSavingsLabel) {
        if (freeIdx >= 0) {
          const freeValue = formatMoney(flatItems[freeIdx].price);
          this.freeSavingsLabel.textContent =
            this.discountState?.state === 'applied'
              ? `Free pack applied — ${freeValue} off`
              : `Free pack — ${freeValue} off at checkout`;
        } else {
          this.freeSavingsLabel.textContent = '';
        }
      }
    } else {
      if (this.priceOriginal) {
        this.priceOriginal.hidden = true;
        this.priceOriginal.textContent = '';
      }
      if (this.priceDiscounted) this.priceDiscounted.textContent = '';
      if (this.savingsLabel) this.savingsLabel.textContent = '';
      if (this.freeSavingsLabel) this.freeSavingsLabel.textContent = '';
    }

    if (this.addToCartButton && !this.addingToCart) {
      if (this.builderType === 'edit') {
        // editing an offer already in the cart — CTA stays enabled/disabled purely on
        // qualification, same as the normal add flow, just with edit-specific copy
        this.addToCartButton.disabled = !ctaQualified;
        this.addToCartButton.textContent = this.editCtaText;
      } else if (this.offerInCart) {
        // offer already in the cart but nothing here matched it for prefill (e.g. its
        // products are no longer offered) — fall back to the original "already claimed" state
        this.addToCartButton.disabled = true;
        this.addToCartButton.textContent = 'Already in Cart';
      } else {
        this.addToCartButton.disabled = !ctaQualified;
        this.addToCartButton.textContent = this.defaultCtaText;
      }
    }
  }

  async onAddToCartClick(event) {
    event.preventDefault();

    if (this.addingToCart) return;

    if (!window.__abc?.addToCart) {
      console.warn('addToCart helper not found');
      return;
    }

    const isEditing = this.builderType === 'edit';

    this.addingToCart = true;
    if (this.addToCartButton) {
      this.addToCartButton.disabled = true;
      this.addToCartButton.textContent = isEditing ? 'Updating...' : 'Adding...';
    }

    const itemProperties = { _offer: this.offerId };
    if (this.rewardValueCents > 0) {
      itemProperties._offer_reward_value = this.rewardValueCents;
    }

    const payload = this.selectedItems.map((item) => ({
      id: item.variantId,
      quantity: item.quantity,
      properties: itemProperties,
    }));

    try {
      if (isEditing && this.bundleToEdit.length > 0) {
        // clear out the offer's existing line items first so the fresh selection replaces
        // them outright, rather than stacking on top (mirrors subscription-builder's edit flow)
        await this.quickRemove(this.bundleToEdit);
      }

      await window.__abc.addToCart(payload);

      // the offer has been rewritten fresh — back to the normal add flow until the next prefill
      this.builderType = 'new';
      this.bundleToEdit = [];

      this.dispatchEvent(
        new CustomEvent('quick-add-builder-add-to-cart', {
          bubbles: true,
          composed: true,
        })
      );
    } catch (err) {
      console.error('Error adding bundle to cart:', err);
    } finally {
      this.addingToCart = false;
      this.updateUI();
    }
  }

  /**
   * Removes cart line items by setting their quantity to 0 via /cart/update.js. Uses the
   * theme's global fetchConfig() helper (global.js) — same as cart.js's removeBulk/updateQuantity
   * — so the Accept header matches what Shopify expects and errors come back as JSON, not an
   * HTML error page.
   * @param {Array} items — cart line items (must carry `.key`)
   */
  quickRemove(items) {
    const updates = items.reduce((acc, item) => {
      acc[item.key] = 0;
      return acc;
    }, {});

    return fetch(routes.cart_update_url, {
      ...fetchConfig(),
      body: JSON.stringify({ updates }),
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          throw new Error(response.description || 'Failed to remove items from cart');
        }
        return response;
      });
  }

  onRemoveClick(event) {
    const btn = event.target.closest('[data-action="remove"]');
    if (!btn) return;
    const productId = btn.getAttribute('data-product-id');
    const variantId = btn.getAttribute('data-variant-id');
    this.dispatchEvent(
      new CustomEvent('quick-add-builder-remove-item', {
        detail: { productId, variantId },
        bubbles: true,
        composed: true,
      })
    );
  }
}

if (!customElements.get('quick-add-builder-footer'))
  customElements.define('quick-add-builder-footer', QuickAddBuilderFooter);
